import EventEmitter from "./EventEmitter";
import EditorExperience from "../InteractiveExperience";
import { has } from "lodash";
// ThreeJS Classes
import Floorplan from "../threeUtils/Floorplan/Floorplan";
import FloorplanV1 from "../threeUtils/Floorplan/FloorplanV1";
import LocationPin from "../threeUtils/LocationPins/LocationPin";
import Connector from "../threeUtils/Connectors/ConnectorPin";

export default class AsyncObjectLoader extends EventEmitter {
    constructor(scene, metadata, pins, connectors, interactiveParent) {
        super();

        this.editor = new EditorExperience();
        this.scene = scene;
        this.interactiveParent = interactiveParent;

        this.sources = [];

        this.items = {};
        this.toLoad = 0;
        this.loaded = 0;

        this.asyncObject = [];

        this.generateMapContent(metadata, pins, connectors);

        this.editor.on("clearAndReset", this.onClearAndReset);
        this.editor.on("asyncObjectLoaded", this.onAsyncObjectLoaded);
    }

    generateMapContent = (metadata, pins, connectors) => {
        let mapContent = {};
        if (metadata && metadata.floors) {
            let floors = [
                {
                    id: "metadata_floorplans",
                    floorplans: metadata.floors,
                },
            ];
            mapContent = { ...mapContent, floors };
        }

        if (pins && pins.length) {
            let locationPins = pins.filter(
                (pin) => pin.pinType !== "connector"
            );
            mapContent = { ...mapContent, locationPins };
        }

        if (connectors && connectors.length) {
            let connectorPins = connectors.filter(
                (pin) => pin.pinType === "connector"
            );
            mapContent = { ...mapContent, connectorPins };
        }

        this.initiateLoading(mapContent);
    };

    onClearAndReset = () => {
        this.loaded = 0;
        this.items = {};
        this.sources = [];
    };

    initiateLoading = (sources) => {
        this.sources = this.toLinearArray(sources);
        this.toLoad = this.sources.length;
        this.sources.length > 0 ? this.startLoading() : this.isReady();
    };

    toLinearArray = (sources) => {
        let arr = [];
        for (const source in sources) {
            if (
                source !== "textNote3Ds" &&
                source !== "nftItems" &&
                source !== "floorplans" &&
                source !== "destination3ds"
            ) {
                arr = [...arr, ...sources[source]];
            }
        }
        //Make Array unique!
        return [...new Map(arr.map((item) => [item["id"], item])).values()];
    };

    startLoading = () => {
        for (const source of this.sources) {
            if (source.id && source.id.includes("floorplan")) {
                if (!(source.id in this.items)) {
                    if (source.id.includes("metadata_floorplans")) {
                        const len = source.floorplans.length;
                        const floor = source.floorplans[len - 1];
                        if (
                            len > 0 &&
                            this.asyncObject.indexOf(floor.id) === -1
                        ) {
                            if ("floorplanVersion" in floor) {
                                this.editor.floorplanVersion =
                                    floor.floorplanVersion;
                            }
                            if (this.editor.floorplanVersion === 2.6) {
                                let metaFloorplans = [];
                                let metaFPlan = new FloorplanV1(
                                    floor,
                                    "floorplan",
                                    true
                                );
                                metaFloorplans.push(metaFPlan.imageMesh);
                                metaFPlan.alignmentStatus &&
                                    metaFloorplans.push(metaFPlan.pegMesh);
                                this.objectLoaded(
                                    source,
                                    source.id,
                                    "floorplan",
                                    metaFloorplans
                                );
                            } else {
                                new Floorplan(floor, "floorplan", this.interactiveParent);
                                this.asyncObject.push(floor.id);
                            }
                        } else {
                            this.objectLoaded(
                                source,
                                "SKIPPED_OBJECT",
                                "SKIPPED_OBJECT",
                                null
                            );
                        }
                    }
                }
            } else if (
                has(source, "pinType") &&
                source.pinType !== "connector"
            ) {
                if (!(source.id in this.items)) {
                    new LocationPin(source);
                    this.asyncObject.push(source.id);
                }
            } else if (
                has(source, "pinType") &&
                source.pinType === "connector"
            ) {
                if (!(source.id in this.items)) {
                    new Connector(source);
                    this.asyncObject.push(source.id);
                }
            } else {
                //Skip this object
                this.objectLoaded(
                    source,
                    "SKIPPED_OBJECT",
                    "SKIPPED_OBJECT",
                    null
                );
            }
        }
    };

    onAsyncObjectLoaded = (object, type) => {
        if (type === "floorplan") {
            const idx = this.asyncObject.indexOf(object.id);
            if (!(object.id in this.items) && idx !== -1) {
                let metaFloorplans = [];
                this.asyncObject.splice(idx, 1);
                metaFloorplans.push(object.imageMesh);

                this.objectLoaded(
                    object,
                    object.id,
                    "floorplan",
                    metaFloorplans
                );
            }
        } else if (type === "locationPin") {
            const idx = this.asyncObject.indexOf(object.id);
            if (!(object.id in this.items) && idx !== -1) {
                this.asyncObject.splice(idx, 1);
                this.editor.uiObjects.push(object);
                object.annotationRenderer.position.copy(object.mesh.position);
                this.objectLoaded(object, object.id, "mapPin", [
                    object.mesh,
                    object.annotationRenderer,
                ]);
            }
        } else if (type === "connectorPin") {
            const idx = this.asyncObject.indexOf(object.id);
            if (!(object.id in this.items) && idx !== -1) {
                this.asyncObject.splice(idx, 1);
                this.editor.uiObjects.push(object);
                // object.annotationRenderer.position.copy(object.mesh.position)
                this.objectLoaded(
                    object,
                    object.id,
                    "connectorPin",
                    object.mesh
                );
            }
        } else if (type === "SKIPPED_OBJECT") {
            this.objectLoaded(object, "SKIPPED_OBJECT", "SKIPPED_OBJECT", null);
        }
    };

    objectLoaded = (source, id, type, file, toAdd = true) => {
        if (id === "SKIPPED_OBJECT") {
            this.loaded++;
        } else {
            if (source.id in this.items) return;
            this.items[id] = {
                type,
                object: file,
            };
            this.loaded++;

            if (toAdd) {
                if (file instanceof Array) {
                    for (const obj of file) {
                        this.interactiveParent.add(obj);
                    }
                } else {
                    this.interactiveParent.add(file);
                }
            }
        }

        // console.log("ITEMS", this.items, this.toLoad, this.loaded);

        this.isReady();
    };

    isReady = () => {
        if (this.loaded === this.toLoad) {
            console.log("All Objects Loaded", this.scene.name);
            this.editor.sceneObjectData[this.scene.name].loading = false;
            this.editor.sceneObjectData[this.scene.name].isLoaded = true;
            this.editor.loadedScenes.push(this.scene.name);
        }
    };
}
